import { useMemo, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { IconSearch } from '@tabler/icons-react';

import DateRangePicker from '@/components/dateRangePicker';
import ExportDropdown from '@/components/ExportDropdown';
import Input from '@/components/Input/Input';
import ProgressBar from '@/components/ProgressBar';
import Select from '@/components/Select';
import SkeletonLoader from '@/components/SkeletonLoader';
import CustomTable from '@/components/Table';
import { EExportType } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import {
  getClassDropdown,
  getClassAnalytics,
  exportAnalyticsData,
} from '@/service/classService';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { downloadFile, formatLastActive } from '@/utils';

interface IClassReport {
  id: string;
  name: string;
  total_teachers: number;
  lessons: number;
  active_students: number;
  avg_completion: number;
  last_update: number;
}

interface IClassesTabForm {
  search: string;
}

const ClassesTab = () => {
  const { formatText } = useFormatText();

  const { register, watch } = useForm<IClassesTabForm>({
    defaultValues: {
      search: '',
    },
  });

  const { isScreenLoading, isTableLoading, setScreenLoading, setTableLoading } =
    useSkeletonStore();

  const [selectedClass, setSelectedClass] = useState<string[]>([]);

  const [classOptions, setClassOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const [classData, setClassData] = useState<IClassReport[]>([]);
  const [totalItems, setTotalItems] = useState(0);

  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [isExportLoading, setIsExportLoading] = useState(false);

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'ASC' | 'DESC';
  } | null>(null);

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'ASC' ? 'DESC' : 'ASC' };
      }
      return { key, direction: 'ASC' };
    });
  };

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await getClassDropdown();
        if (response?.status && Array.isArray(response.data)) {
          const options = response.data.map(
            (c: { id: string; name: string }) => ({
              value: c.id,
              label: c.name,
            })
          );
          setClassOptions(options);
        }
      } catch (error) {
        console.error('Failed to fetch classes:', error);
      }
    };
    fetchClasses();
  }, []);

  const searchQuery = watch('search');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const fetchAnalytics = async () => {
    if (classData.length === 0 && !debouncedSearchQuery) {
      setScreenLoading(true);
    } else {
      setTableLoading(true);
    }
    try {
      const query: Record<string, unknown> = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      };

      if (debouncedSearchQuery) query.search = debouncedSearchQuery;
      if (selectedClass.length > 0) query.class_id = selectedClass;
      if (startDate)
        query.start_date = Math.floor(new Date(startDate).getTime() / 1000);
      if (endDate)
        query.end_date = Math.floor(new Date(endDate).getTime() / 1000);

      if (sortConfig?.key && sortConfig?.direction) {
        query[`order[${sortConfig.key}]`] = sortConfig.direction;
      } else {
        query[`order[created_at]`] = 'DESC';
      }

      const response = await getClassAnalytics(query);
      if (response?.status) {
        setClassData(response.data || []);
        setTotalItems(response?.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch class analytics:', error);
    } finally {
      setScreenLoading(false);
      setTableLoading(false);
      if (isFirstLoad) {
        setIsFirstLoad(false);
      }
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    debouncedSearchQuery,
    selectedClass,
    startDate,
    endDate,
    pageSize,
    isFirstLoad,
    sortConfig,
  ]);

  const handleExport = async (doc_type: 'csv' | 'pdf') => {
    setIsExportLoading(true);
    try {
      const query: Record<string, unknown> = {
        type: 1,
      };
      if (selectedRowKeys.length > 0) {
        query.ids = selectedRowKeys;
      } else {
        if (debouncedSearchQuery) query.search = debouncedSearchQuery;
        if (selectedClass.length > 0) query.class_id = selectedClass;
        if (startDate)
          query.start_date = Math.floor(new Date(startDate).getTime() / 1000);
        if (endDate)
          query.end_date = Math.floor(new Date(endDate).getTime() / 1000);
      }
      if (doc_type === 'csv') {
        query.doc_type = EExportType.CSV;
      } else if (doc_type === 'pdf') {
        query.doc_type = EExportType.PDF;
      }

      const response = await exportAnalyticsData(query);
      if (response) {
        const label = formatText('reports-analytics.tabs.classes')
          .replace(/\s+/g, '_')
          .toLowerCase();
        downloadFile(response, `${label}_${new Date().getTime()}.${doc_type}`);
      }
    } catch (error) {
      console.error('Failed to export class analytics:', error);
    } finally {
      setIsExportLoading(false);
    }
  };

  const classColumns = useMemo(
    () => [
      {
        key: 'name',
        title: 'reports-analytics.table.class-name',
        width: '260px',
        sorter: true,
        handleSort: () => handleSort('name'),
        render: (record: IClassReport) => (
          <span className="text-sm font-semibold text-neutral-primary">
            {record.name}
          </span>
        ),
      },
      {
        key: 'total_teachers',
        title: 'reports-analytics.table.assigned-teachers',
        align: 'center' as const,
        width: '260px',
        sorter: true,
        handleSort: () => handleSort('total_teachers'),
        render: (record: IClassReport) => (
          <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-orange-light-50 text-orange-secondary text-sm font-semibold">
            {record.total_teachers}
          </span>
        ),
      },
      {
        key: 'lessons',
        title: 'reports-analytics.table.lessons-created',
        align: 'center' as const,
        width: '260px',
        sorter: true,
        handleSort: () => handleSort('lessons'),
        render: (record: IClassReport) => (
          <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-pink-light-50 text-pink-secondary text-sm font-semibold">
            {record.lessons}
          </span>
        ),
      },
      {
        key: 'active_students',
        title: 'reports-analytics.table.active-students',
        align: 'center' as const,
        width: '260px',
        sorter: true,
        handleSort: () => handleSort('active_students'),
        render: (record: IClassReport) => (
          <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-success-light-50 text-success-secondary text-sm font-semibold">
            {record.active_students}
          </span>
        ),
      },
      {
        key: 'avg_completion',
        title: 'reports-analytics.table.avg-completion',
        align: 'center' as const,
        width: '260px',
        sorter: true,
        handleSort: () => handleSort('avg_completion'),
        render: (record: IClassReport) => (
          <div className="flex flex-col gap-1">
            <ProgressBar value={record?.avg_completion ?? 0} />
          </div>
        ),
      },
      {
        key: 'last_update',
        title: 'reports-analytics.table.last-update',
        align: 'start' as const,
        width: '260px',
        sorter: true,
        handleSort: () => handleSort('last_update'),
        render: (record: IClassReport) => (
          <span className="text-sm text-neutral-secondary whitespace-nowrap">
            {formatLastActive(record.last_update, formatText)}
          </span>
        ),
      },
    ],

    [formatText]
  );

  const isFilterApplied =
    !!debouncedSearchQuery ||
    selectedClass.length > 0 ||
    !!startDate ||
    !!endDate;

  const hasNoClasses =
    !isFirstLoad &&
    !isScreenLoading &&
    classData.length === 0 &&
    !isFilterApplied;

  const isClassEmptySearch =
    !isFirstLoad &&
    !isScreenLoading &&
    classData.length === 0 &&
    isFilterApplied;

  return (
    <div className="flex flex-col gap-6" data-testid="classes-tab-container">
      {/* Filters Section */}
      {!hasNoClasses && (
        <div
          className="flex items-start justify-between gap-6 flex-wrap ps-1"
          data-testid="classes-tab-filters"
        >
          <div className="flex flex-1 items-center gap-2.5 min-w-87.5 flex-wrap">
            {isScreenLoading ? (
              <SkeletonLoader className="h-11 w-87.5 rounded-lg" />
            ) : (
              <Input
                name="search"
                placeholder={formatText('reports-analytics.class-searchbar')}
                leftIcon={{
                  icon: <IconSearch size={18} className="text-neutral-400" />,
                }}
                register={register}
                dataTestId="class-report-search-input"
                className="max-w-70"
                inputClassName="py-[13px]"
              />
            )}

            {isScreenLoading ? (
              <SkeletonLoader className="h-11 w-37.5 rounded-lg" />
            ) : (
              <Select
                options={classOptions}
                selected={selectedClass}
                isClearable
                isSearch
                isMulti
                onChange={(val) => setSelectedClass(val as string[])}
                placeholder={formatText('reports-analytics.filters.class')}
                className="min-w-37.5! py-3.25"
                dataTestId="class-report-filter-select"
              />
            )}
          </div>

          <div
            className="flex items-center gap-2.5"
            data-testid="classes-tab-actions"
          >
            {isScreenLoading ? (
              <SkeletonLoader className="h-11 w-48 rounded-lg" />
            ) : (
              <DateRangePicker
                popupAlign="right"
                popUpClassName="right-0! rtl:left-0 rtl:right-auto"
                setFirstDate={setStartDate}
                setSecondDate={setEndDate}
                dataTestId="date-range-picker"
              />
            )}

            {isScreenLoading ? (
              <SkeletonLoader className="h-11 w-32 rounded-lg" />
            ) : (
              <ExportDropdown
                onCsvExport={() => handleExport('csv')}
                onPdfExport={() => handleExport('pdf')}
                disabled={classData?.length === 0 || isExportLoading}
              />
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div
        className="bg-white rounded-2xl w-full"
        data-testid="classes-report-table"
      >
        <CustomTable
          dataSource={classData}
          columns={classColumns}
          rowKey="id"
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          total={totalItems}
          pageSize={pageSize}
          selectedRowKeys={selectedRowKeys}
          onSelectionChange={setSelectedRowKeys}
          isEmptySearch={isClassEmptySearch}
          isEmpty={hasNoClasses}
          showHeader={!hasNoClasses}
          hidePagination={hasNoClasses}
          emptyImgSrc="/not-found.svg"
          emptyTitle={formatText('reports-analytics.empty-title')}
          emptyDescription={formatText('reports-analytics.empty-description')}
          isLoading={isTableLoading || isFirstLoad}
          fieldName={sortConfig?.key}
          sortDirection={sortConfig?.direction}
          dataTestId="classes-report-table-component"
        />
      </div>
    </div>
  );
};

export default ClassesTab;
